import { Link } from "react-router";
import { ArrowDown, Lock, Sparkles } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Card, ListLink, SectionTitle } from "@/components/ui";
import { useParentJobs, useParentResponsibilities, usePenalties, useRewardTypes, useStore } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { formatUnits, isConvertible } from "@shared/economy";

export function Plan() {
  const { family } = useSession();
  const resp = useParentResponsibilities();
  const jobs = useParentJobs();
  const rewards = useRewardTypes();
  const store = useStore();
  const penalties = usePenalties();

  const respCount = resp.data?.responsibilities.filter((r) => r.active).length ?? 0;
  const jobCount = jobs.data?.jobs.filter((j) => j.status === "active").length ?? 0;
  const convertible = (rewards.data?.rewardTypes ?? []).filter((t) => t.active && isConvertible(t));
  const storeCount = store.data?.items.filter((i) => i.active).length ?? 0;
  const penaltyCount = penalties.data?.presets.length ?? 0;

  return (
    <div>
      <PageHeader title="Plan" subtitle="Design your household economy" bell="/parent/notifications" />

      <Card className="mt-3 bg-gradient-to-br from-brand-soft to-card">
        <div className="text-sm font-extrabold uppercase tracking-wide text-brand-strong">How it works</div>
        <div className="mt-3 space-y-2 text-sm font-semibold">
          <div className="flex items-center gap-2">
            <span className="flex size-7 items-center justify-center rounded-full bg-card text-base">🛏️</span>
            Responsibilities first — no reward, just expected
          </div>
          <ArrowDown className="ml-1.5 size-4 text-muted" />
          <div className="flex items-center gap-2">
            <span className="flex size-7 items-center justify-center rounded-full bg-card">
              <Lock className="size-3.5" />
            </span>
            Then jobs unlock — paid in {family.currencyIcon} {family.currencyName}
          </div>
          <ArrowDown className="ml-1.5 size-4 text-muted" />
          <div className="flex items-center gap-2">
            <span className="flex size-7 items-center justify-center rounded-full bg-card text-base">🎁</span>
            {family.currencyName} become rewards that matter to each kid
          </div>
        </div>
      </Card>

      <SectionTitle>Earning</SectionTitle>
      <Card className="p-1">
        <ListLink to="/parent/plan/responsibilities" icon="🛏️" title="Responsibilities" subtitle={`${respCount} active · the gate to earning`} />
        <ListLink to="/parent/plan/jobs" icon="🧰" title="Jobs" subtitle={`${jobCount} paid job${jobCount === 1 ? "" : "s"} · kids can pitch their own`} />
      </Card>

      <SectionTitle>Spending</SectionTitle>
      <Card className="p-1">
        <ListLink
          to="/parent/plan/rewards"
          icon="💱"
          title="Rewards & limits"
          subtitle={
            convertible.length
              ? convertible
                  .map((t) => `${t.pointsPerUnit} = ${formatUnits(1, t)}${t.kind === "time" ? ` ${t.name.toLowerCase()}` : ""}`)
                  .join(" · ")
              : "Cash, screen time, caps & schedules"
          }
        />
        <ListLink to="/parent/plan/store" icon="🛍️" title="Family store" subtitle={`${storeCount} reward${storeCount === 1 ? "" : "s"} on the menu`} />
      </Card>

      <SectionTitle>Consequences</SectionTitle>
      <Card className="p-1">
        <ListLink
          to="/parent/plan/discipline"
          icon="⚖️"
          title="Discipline rules"
          subtitle={`${penaltyCount} preset${penaltyCount === 1 ? "" : "s"} · ${family.allowNegative ? `floor ${family.negativeFloor}` : "never below zero"}${family.workItOffEnabled ? " · work-it-off on" : ""}`}
        />
      </Card>

      <Link to="/parent/advisor">
        <Card className="mt-6 flex items-center gap-3 bg-violet-soft ring-violet/30 transition hover:brightness-[0.98]">
          <span className="flex size-11 items-center justify-center rounded-2xl bg-violet text-white">
            <Sparkles className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-extrabold">Not sure what's fair?</div>
            <div className="text-sm text-muted">The advisor can price jobs, stock your store, and check your economy.</div>
          </div>
        </Card>
      </Link>
    </div>
  );
}
